<template>
  <a-col flex="auto" class="flex flex-col gap-3">
    <component
      :is="markRaw(COMPONENT_LIST[tab].component)"
      v-model:state="state"
      :committeeData="committeeData"
      :key="tab"
    />
  </a-col>
</template>

<script setup lang="ts">
import {
  getListCandidateInfoById,
  getListForCommittee,
} from "@/apis/register/get";
import { CommitteeType } from "@/apis/register/type";
import type { ApplicantInformationV2State } from "@/components/applicantInformationView/type";
import { getQueryNumber } from "@/utils/helper";
import { message } from "ant-design-vue";
import { computed, defineAsyncComponent, markRaw, onMounted, ref } from "vue";
import { useMeta } from "vue-meta";
import { useRoute, useRouter } from "vue-router";

interface ComponentListItem {
  component: ReturnType<typeof defineAsyncComponent>;
  title: string;
}

const route = useRoute();
const router = useRouter();

const state = ref<ApplicantInformationV2State>({
  title: [""],
  files: [],
});

const committeeData = ref<CommitteeType[]>([]);

onMounted(async () => {
  try {
    const lfcRes = await getListForCommittee();

    if (lfcRes.status !== 200 || !lfcRes.data) {
      message.error("ไม่สามารถโหลดข้อมูลคณะกรรมการได้");
      throw new Error("API: getListForCommittee failed");
    }

    committeeData.value = lfcRes.data;
    const currentTab = tab.value;

    if (currentTab <= 0) return;

    const queryId = getQueryNumber(route.query?.id as string);
    if (!queryId) throw new Error("Invalid or missing query ID");

    const selectedItem = lfcRes.data.find((item) => item?.id === queryId);
    if (!selectedItem) throw new Error("ไม่พบข้อมูลที่ตรงกับ ID");

    const title = [selectedItem.title || "ไม่มีหัวข้อ"];

    if (currentTab === 1) {
      state.value = {
        title: title,
        files: [],
      };
    } else if (currentTab === 2) {
      const lciRes = await getListCandidateInfoById(queryId);

      if (lciRes.status !== 200 || !lciRes.data) {
        message.error("ไม่สามารถโหลดข้อมูลผู้สมัครได้");
        throw new Error("API: getListCandidateInfoById failed");
      }

      let files = [];

      const querySubId = getQueryNumber(route.query?.subId as string);

      if (querySubId) {
        const subItem = lciRes.data.list?.find(
          (item) => item.id === querySubId,
        );

        if (!subItem) throw new Error("ไม่พบข้อมูลย่อยที่ตรงกับ subId");

        title.push(`เอกสารแนบใบสมัคร : ${subItem.fullName}`);
        files =
          subItem?.fileAttachs?.map((file) => ({
            name: file.originalName,
            path: file.path,
          })) || [];
      } else {
        title.push("เอกสารการประชุม");
        files =
          lciRes.data?.fileMeetingAttach?.map((file) => ({
            name: file.title,
            path: file.pathFile,
          })) || [];
      }

      state.value = {
        title,
        files,
      };
    }
  } catch (error) {
    console.error("เกิดข้อผิดพลาด:", error);
    router.replace({ query: { tab: 0 } });
  }
});

useMeta(() => ({
  title: `Applicant Information - ${COMPONENT_LIST[tab.value].title}`,
}));

const PositionListV2 = defineAsyncComponent(
  () => import("@/components/applicantInformationView/PositionListV2.vue"),
);
const ApplicantDetailTableV2 = defineAsyncComponent(
  () =>
    import("@/components/applicantInformationView/ApplicantDetailTableV2.vue"),
);
const UploadListV2 = defineAsyncComponent(
  () => import("@/components/applicantInformationView/UploadListV2.vue"),
);

const COMPONENT_LIST: ComponentListItem[] = [
  {
    component: PositionListV2,
    title: "Position Selection",
  },
  {
    component: ApplicantDetailTableV2,
    title: "Applicant Details",
  },
  {
    component: UploadListV2,
    title: "Document Upload",
  },
];

const tab = computed(() => {
  const queryTab = getQueryNumber(route.query.tab as string);

  return typeof queryTab === "number" &&
    queryTab >= 0 &&
    queryTab < COMPONENT_LIST.length
    ? queryTab
    : 0;
});
</script>
